import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DefaultLayout from '../../layout/DefaultLayout';
import Loading from '../../components/Loading';
import paymentService from '../../services/paymentService';
import { useGlobalState } from '../../state';
import { toCurrency } from '../../utils/formaters';
import Alert from '../../components/Alert';
import Breadcrumb from '../../components/Breadcrumbs/Breadcrumb';
import ActionModal from '../../components/ActionModal';

const STORAGE_KEY = 'pagoPlanTemporal';

const Pagar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = useGlobalState('user');

  const [loading, setLoading] = useState<boolean>(false);
  const [pagoEnviado, setPagoEnviado] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [itemsDeuda, setItemsDeuda] = useState<any[] | undefined>(undefined);
  const [pagoPlanTemporal, setPagoPlanTemporal] = useState<any | undefined>(undefined);
  const [conceptoPlan, setConceptoPlan] = useState<any | undefined>(undefined);
  const [poseePlanVigente, setPoseePlanVigente] = useState<boolean | undefined>(undefined);
  const [montoPagoTemporal, setMontoPagoTemporal] = useState<number | undefined>(undefined);
  const [deuda, setDeuda] = useState<number>(0);
  const [showConfirmPagoModal, setShowConfirmPagoModal] = useState<boolean>(false);

  // Try to read deuda from query string ?deudaAfiliado=1234.56
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const deudaParam = params.get('deudaAfiliado');
    if (deudaParam) {
      setDeuda(parseFloat(String(deudaParam)));
    }
  }, [location.search]);

  useEffect(() => {
    // Redirect to signin when there's no user (but wait if undefined)
    if (user === undefined) return;
    if (!user) {
      navigate('/auth/signin');
      return;
    }

    const fetch = async () => {
      setLoading(true);
      setError(undefined);

      try {
        // concepto plan
        const conceptoRes = await paymentService.getConceptoPlan();
        if (conceptoRes && conceptoRes.success) setConceptoPlan(conceptoRes.data);
        else if (conceptoRes?.errors) setError(conceptoRes.errors[0]);

        // detalle deuda
        const detalleRes = await paymentService.getDetalleDeDeuda?.();
        if (detalleRes && detalleRes.success) {
          const filtered = (detalleRes.data || []).filter((i: any) => i.totalPdo != 0);
          setItemsDeuda(filtered);
        } else if (detalleRes?.errors) {
          setError(detalleRes.errors[0]);
        }

        // if deuda wasn't passed via query, try to fetch current deuda
        if (!deuda || deuda === 0) {
          const deudaRes = await paymentService.getDeudaActual?.();
          if (deudaRes && deudaRes.success && typeof deudaRes.data === 'number') {
            setDeuda(deudaRes.data);
          } else if (deudaRes?.success && deudaRes.data && typeof deudaRes.data.montoTotal === 'number') {
            // some endpoints return object with montoTotal
            setDeuda(deudaRes.data.montoTotal);
          }
        }
      } catch (ex: any) {
        setError(ex?.message ?? 'Ocurrió un error al cargar la información');
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [user, navigate]);

  const pagarDeuda = async () => {
    setLoading(true);
    setError(undefined);

    try {
      if (!user) {
        setError('Usuario no identificado');
        return;
      }
      if (!conceptoPlan) {
        setError('No se pudo obtener el concepto de plan');
        return;
      }

      // Crear Plan de Pago
      const responsePlan = await paymentService.crearPlanPago();
      if (responsePlan && responsePlan.success) {
        // preserve montoOriginal
        responsePlan.data = responsePlan.data || {};
        responsePlan.data.montoOriginal = parseFloat(String(deuda ?? 0));

        const formData = new FormData();
        formData.append('TipoPago', String('MERCADOPAGO_PLAN')); // same identifier as backend expects
        formData.append('IdPlanPago', String(responsePlan.data.id));
        formData.append('Monto', String(responsePlan.data.monto));
        formData.append('Concepto', String(conceptoPlan.id));
        formData.append(
          'Metadata',
          JSON.stringify({
            descripcion: conceptoPlan.descripcion,
            idPlanPago: responsePlan.data.id,
          })
        );

        // Callbacks for success/failure
        const baseUrl = window.location.origin;
        formData.append('URLCallBackExito', `${baseUrl}/pagos/callbackPagoPlanExito`);
        formData.append('URLCallBackFalla', `${baseUrl}/pagos/callbackPagoPlanFalla`);

        const response = await paymentService.crearPago(formData);
        if (response && response.success) {
          response.data.montoOriginal = parseFloat(String(deuda ?? 0));
          response.data.periodo = response.data.periodo ?? '';
          response.data.concepto = conceptoPlan.id;
          response.data.metadata = {
            descripcion: conceptoPlan.descripcion,
            idPlanPago: responsePlan.data.id,
          };
          setPagoPlanTemporal(response.data);
        } else {
          setError(response?.errors?.[0] ?? 'Ocurrió un error al generar el pago');
        }
      } else {
        // Manejo cuando ya existe plan vigente
        const errMsg = responsePlan?.errors?.[0] ?? '';
        if (errMsg && errMsg.includes('posee un plan vigente')) {
          const pago = localStorage.getItem(STORAGE_KEY);
          if (pago) {
            setPoseePlanVigente(true);
            try {
              setMontoPagoTemporal(JSON.parse(pago).monto);
            } catch {
              setMontoPagoTemporal(undefined);
            }
          } else {
            setError(errMsg);
          }
        } else {
          setError(responsePlan?.errors?.[0] ?? 'Ocurrió un error al crear el plan');
        }
      }
    } catch (ex: any) {
      setError(ex?.message ?? 'Ocurrió un error inesperado');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pagoPlanTemporal) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pagoPlanTemporal));
      } catch {
        // ignore storage errors
      }

      // If amount didn't change, open MercadoPago immediately
      if (pagoPlanTemporal.monto === pagoPlanTemporal.montoOriginal) {
        setPagoEnviado(true);
        if (pagoPlanTemporal.url) {
          window.open(pagoPlanTemporal.url, '_blank', 'noopener');
        }
      }
    }
  }, [pagoPlanTemporal]);

  const abrirMercadoPago = () => {
    if (!pagoPlanTemporal) return;
    setPagoEnviado(true);
    if (pagoPlanTemporal.url) window.open(pagoPlanTemporal.url, '_blank', 'noopener');
  };

  const pagarPlanExistente = () => {
    const pago = localStorage.getItem(STORAGE_KEY);
    if (pago) {
      try {
        setPagoPlanTemporal(JSON.parse(pago));
      } catch {
        setError('No se pudo recuperar el pago temporal');
      }
    } else {
      setError('No se encontró un plan temporal para pagar');
    }
  };

  const handleAbrirConfirmacionPago = () => {
    setShowConfirmPagoModal(true);
  };

  const handleCancelarConfirmacionPago = () => {
    setShowConfirmPagoModal(false);
  };

  const handleConfirmarPagoDeuda = async () => {
    setShowConfirmPagoModal(false);
    await pagarDeuda();
  };

  return (
      <DefaultLayout>
          <Breadcrumb pageName="Pagar" />

      {error && <Alert type="failure" message={error} />}

      <div className="p-6">
        <div className="md:max-w-6xl mx-auto bg-white md:p-6 rounded">
     

          {loading && <Loading statusText="Cargando" />}

          {/* Plan vigente */}
          {poseePlanVigente && !pagoEnviado ? (
            <div className="mb-6">
              <p className="text-base font-semibold mb-4">
                Ya tienes un plan de pago vigente por un monto de {toCurrency(montoPagoTemporal ?? 0)}
              </p>
              <p className="text-sm text-gray-600 mb-4">¿Deseas continuar con el pago de este plan?</p>
              <p className="text-sm text-gray-600 mb-6">
                En caso de no querer continuar con este plan de pagos, contactanos para solicitar la cancelación del plan.
              </p>

              <button
                onClick={pagarPlanExistente}
                disabled={loading}
                className="bg-primario-celeste-800 text-white font-semibold px-4 py-2 rounded"
              >
                {loading ? 'Procesando...' : 'Continuar con el pago'}
              </button>
            </div>
          ) : null}

          {/* Mostrar deuda y detalle */}
          {!pagoEnviado && !pagoPlanTemporal && !poseePlanVigente && (
            <>
              <p className="text-sm text-gray-500 mb-1">Saldo a pagar de la cuenta</p>
              <div className="text-3xl font-bold text-primario-celeste-800 mb-4">{toCurrency(Number(deuda ?? 0))}</div>              

              {!loading ? (
                itemsDeuda && !error ? (
                  <div className="bg-gray-50 rounded">
                    <div className="grid grid-cols-4 gap-2 p-3 bg-gray-100 font-semibold text-sm">
                      <div>Período</div>
                      <div>$ Deuda</div>
                      <div>$ Interés</div>
                      <div className="text-right">$ Total Período</div>
                    </div>

                    <div>
                      {itemsDeuda.map((item: any) => (
                        <div key={item.idCta} className="grid grid-cols-4 gap-2 p-3 border-b border-gray-200">
                          <div className="text-sm">{item.periodo}</div>
                          <div className="text-sm">{toCurrency(item.montoDeuda)}</div>
                          <div className="text-sm">{toCurrency(item.interes)}</div>
                          <div className="text-sm text-right font-bold">{toCurrency(item.totalPdo)}</div>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-end items-center p-3">
                      <div className="mr-4 font-semibold">TOTAL</div>
                      <div className="text-2xl font-extrabold text-primario-celeste-800">{toCurrency(Number(deuda ?? 0))}</div>
                    </div>
                  </div>
                ) : null
              ) : null}

              {/* Botón de pago */}
              {!error && (
                <div className="mt-6 text-center">
                  <button
                    onClick={handleAbrirConfirmacionPago}
                    disabled={loading || (itemsDeuda?.length ?? 0) === 0}
                    className={`px-6 py-3 w-[200px] rounded font-semibold text-white ${loading || (itemsDeuda?.length ?? 0) === 0 ? 'bg-gray-400' : 'bg-primario-celeste-800'}`}
                  >
                    {loading ? 'Procesando...' : 'Pagar'}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Pago enviado */}
          {pagoEnviado && pagoPlanTemporal && (
            <div className="mt-6">
              <p className="text-sm">Has sido redirigido a Mercado Pago!</p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/')}
                  disabled={loading}
                  className="px-6 py-3 rounded font-semibold text-white bg-primario-celeste-800"
                >
                  Volver
                </button>
              </div>
            </div>
          )}

          {/* Confirmar monto cuando hubo intereses */}
          {!pagoEnviado && pagoPlanTemporal && pagoPlanTemporal.monto !== pagoPlanTemporal.montoOriginal && (
            <div className="mt-6 text-center">
              <h3 className="text-xl font-semibold mb-2">Confirmar monto a pagar:</h3>
              {pagoPlanTemporal.montoOriginal !== pagoPlanTemporal.monto && (
                <p className="text-sm text-gray-600 mb-4">Se han sumado intereses por deuda pendiente y el monto a pagar es de:</p>
              )}

              <div className="text-4xl font-extrabold text-primario-celeste-800 mb-6">{toCurrency(pagoPlanTemporal.monto)}</div>

              <button
                onClick={abrirMercadoPago}
                disabled={loading}
                className="px-6 py-3 rounded font-semibold text-white bg-primario-celeste-800"
              >
                Confirmar
              </button>
            </div>
          )}
        </div>
        <div className="flex gap-3 justify-center mt-3">
            <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded"
            >
                Volver
            </button>
        </div>
      </div>

      {/* Modal de confirmación de pago */}
      {showConfirmPagoModal && (
        <ActionModal
          open={showConfirmPagoModal}
          variant="warning"
          title="Confirmar pago"
          message="Estás a punto de consolidar un convenio para realizar el pago total de tu deuda. ¿Estás seguro de continuar?"
          primaryButton={{
            label: 'Si',
            onClick: handleConfirmarPagoDeuda,
          }}
          secondaryButton={{
            label: 'No',
            onClick: handleCancelarConfirmacionPago,
            variant: 'secondary',
          }}
          onClose={handleCancelarConfirmacionPago}
          showCloseIcon
        />
      )}
    </DefaultLayout>
  );
};

export default Pagar;